import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface PriceTier {
  id: string;
  product_id: string;
  min_quantity: number;
  price: number;
}

/**
 * Hook to fetch tiered pricing for products
 * @param productId - Optional specific product ID to fetch tiers for
 * @returns Object containing tiered pricing data and loading state
 */
export function useProductPriceTiers(productId?: string) {
  const [priceTiers, setPriceTiers] = useState<PriceTier[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPriceTiers = async () => {
      setLoading(true);
      setError(null);
      
      try {
        let query = supabase
          .from('product_price_tiers')
          .select('*')
          .order('min_quantity', { ascending: true });
        
        // If a productId is provided, filter to just that product
        if (productId) {
          query = query.eq('product_id', productId);
        }
        
        const { data, error: fetchError } = await query;
        
        if (fetchError) {
          throw fetchError;
        }
        
        setPriceTiers(data || []);
      } catch (err: any) {
        console.error('Error fetching price tiers:', err);
        setError(err.message || 'Failed to fetch price tiers');
      } finally {
        setLoading(false);
      }
    };

    fetchPriceTiers();
  }, [productId]);

  /**
   * Get the appropriate price for a product based on quantity
   * @param productId - The product ID
   * @param quantity - Quantity being ordered
   * @param basePrice - Base price to use if no tier is applicable
   * @returns The applicable price for the quantity
   */
  const getPriceForQuantity = (productId: string, quantity: number, basePrice: number): number => {
    // Filter tiers for this product and find the highest applicable tier
    const applicableTiers = priceTiers
      .filter(tier => tier.product_id === productId && tier.min_quantity <= quantity)
      .sort((a, b) => b.min_quantity - a.min_quantity);
    
    // If we have an applicable tier, use its price, otherwise use base price
    return applicableTiers.length > 0 ? applicableTiers[0].price : basePrice;
  };

  return { priceTiers, loading, error, getPriceForQuantity };
} 